/**
 * Личный кабинет: кто заведён, когда входил, что делал — и выход из системы.
 *
 * УДАЛЕНИЯ УЧЁТНОЙ ЗАПИСИ ЗДЕСЬ НЕТ, потому что его нет и в контракте: на `app_user.id`
 * ссылаются решения, ключи и строки журнала, который живёт не меньше полугода (NFR-2).
 * Уход человека — это `is_active = 0`: вход запрещён, сессии отозваны, история цела. Кнопка
 * «удалить», которая на самом деле отключает, врала бы о последствиях, поэтому она названа
 * своим именем, а одна строка внизу объясняет, почему именно так.
 *
 * ПРАВА НЕ ВЫЧИСЛЯЮТСЯ ПО РОЛИ. Что человеку можно, говорит `can_manage` из ответа: роль он
 * знает и сам, но право — не роль, и второй ответ на тот же вопрос разошёлся бы с первым.
 * Не-администратор видит список из одной строки — своей: это тот же экран, а не урезанная
 * копия, и «а кто ещё есть» ему просто не отвечают.
 *
 * ХЭШЕЙ ПАРОЛЕЙ ЗДЕСЬ НЕТ И ПОКАЗАТЬ ИХ НЕЧЕМ: в `AccountSchema` их не существует, есть лишь
 * момент установки. Показывается ответ на «пароль вообще задан?», а не сам пароль.
 */

import { DRAFT_STATES, type DraftState } from '@smm/contracts';
import { ACCOUNT_LOGIN_RE, ACCOUNT_PASSWORD_MIN } from '@smm/contracts/schemas';
import { type JSX, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'wouter';
import { ApiRequestError, post } from '../api/client.ts';
import type {
  Account,
  AccountActivityEntry,
  AccountActivityResponse,
  AccountRole,
  AccountsResponse,
} from '../api/types.ts';
import { useResource } from '../api/useResource.ts';
import { useSession } from '../session.tsx';
import { initials, shortMsk } from './format.ts';
import { Modal } from './Modal.tsx';
import styles from './ProfileDialog.module.css';
import { statusLabel } from './status.ts';

const ROLE_LABEL: Readonly<Record<AccountRole, string>> = {
  approver: 'согласующий',
  admin: 'администратор',
};

/**
 * Роль сессии приходит строкой (`session.tsx`), а не `AccountRole`: сессия — это ответ
 * сервера, и приведением типа его не проверить. Незнакомая роль показывается как есть.
 */
const roleWord = (role: string): string =>
  role === 'admin' || role === 'approver' ? ROLE_LABEL[role] : role;

/**
 * Что человек сделал — глаголом. Список открыт: типы событий заводит бэкенд, и незнакомый
 * код показывается как есть — пустая строка в журнале хуже незнакомого слова.
 */
const EVENT_LABEL: Readonly<Record<string, string>> = {
  'approval_key.issued': 'одобрил',
  'rework.requested': 'отправил на переделку',
  'rework.closed': 'закрыл переделку',
  'draft.created': 'завёл пост',
  'draft.edited_by_human': 'правил текст',
  'draft.quality_marked': 'оценил',
  'draft_version.created': 'создал версию',
  'publication.withdrawn': 'отозвал публикацию',
  'publication_dispatch.changed': 'переключил режим публикаций',
  // Автоодобрение бренда (docs/ai-lab.md): переключает только администратор. Выключение
  // бэкендом на старте (`auto_approval.forced_off`) — событие системы, в журнал человека не попадает.
  'auto_approval.changed': 'переключил автоодобрение',
  // Мнение автора канала (AGI Boardroom, 2026-11-02, `api/author-opinion.ts`) — слова кнопок
  // полосы «Мнение автора». «Изменить мнение» — то же событие, что «Добавить»: новое мнение
  // заменяет прежнее. Снятое правкой текста (`detached`) — следствие правки человека, и в его
  // журнале оно стоит рядом с «правил текст».
  'author_opinion.submitted': 'добавил мнение',
  'author_opinion.remove_requested': 'убрал мнение',
  'author_opinion.detached': 'убрал мнение правкой текста',
  // Остальные события строки мнения (бэкенд, services/backend/src/db/audit.ts) в журнал человека
  // попадают, когда их вызвала его же версия: правка, картинка или файл закрыли переделку мнения.
  'author_opinion.superseded': 'заменил мнение',
  'author_opinion.inserted': 'вставил мнение в текст',
  'author_opinion.removed': 'убрал мнение из текста',
  'author_opinion.cancelled': 'отменил вставку мнения',
  'author_opinion.remove_cancelled': 'отменил снятие мнения',
  'writer_model_override.changed': 'сменил модель писателя',
  // Партия 19.09.2026: «только мониторинг» и стили как данные (docs/release-2026-09-19.md
  // §4, §5). Слова — те же, что на кнопках: журнал читают, чтобы найти нажатие.
  'monitoring_scan.requested': 'запустил только мониторинг',
  'style_version.created': 'сделал копию стиля',
  'style_version.updated': 'изменил версию стиля',
  'style_version.file_saved': 'правил файл стиля',
  'style_default.changed': 'сменил стиль по умолчанию',
  'content_plan.updated': 'правил контент-план',
  'generation_request.created': 'запросил генерацию',
  'slot.created': 'добавил слот',
  'slot.deleted': 'убрал слот',
  'slot.moved': 'перенёс пост',
  'topic.created': 'завёл тему',
  'topic.status_changed': 'менял статус темы',
  'user.created': 'завёл учётную запись',
  'user.password_set': 'сменил пароль',
  'user.active_changed': 'менял доступ',
};

const isDraftState = (s: string): s is DraftState =>
  (DRAFT_STATES as readonly string[]).includes(s);
const stateWord = (s: string): string => (isDraftState(s) ? statusLabel(s) : s);

function actionOf(e: AccountActivityEntry): string {
  if (e.event_type === 'draft.status_changed' && e.to_status !== null) {
    return e.from_status === null
      ? `статус: ${stateWord(e.to_status)}`
      : `${stateWord(e.from_status)} → ${stateWord(e.to_status)}`;
  }
  return EVENT_LABEL[e.event_type] ?? e.event_type;
}

const errorText = (e: unknown): string =>
  e instanceof ApiRequestError || e instanceof Error ? e.message : String(e);

/**
 * Размытие фона. Отдельным слоем, а не свойством `.dim`: затемнение принадлежит общему
 * диалогу (Modal + base.css), и добавлять размытие туда значит менять вид всех диалогов
 * решения ради одного окна. Слой лежит НИЖЕ диалога (z-index 39 против 40), поэтому
 * размывает страницу, а не карточку.
 */
function BackdropBlur(): JSX.Element {
  return createPortal(<div className={styles['blur']} aria-hidden="true" />, document.body);
}

export function ProfileDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const { user, logout } = useSession();
  const [, navigate] = useLocation();

  const accounts = useResource<AccountsResponse>('/api/v1/accounts');
  const [picked, setPicked] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pwFor, setPwFor] = useState<string | null>(null);
  const [confirmOff, setConfirmOff] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const me = user?.id ?? null;
  const target = picked ?? me;
  const activity = useResource<AccountActivityResponse>(
    target === null ? null : `/api/v1/accounts/${encodeURIComponent(target)}/activity?limit=20`,
  );

  const list = accounts.data?.accounts ?? [];
  const canManage = accounts.data?.can_manage ?? false;
  const mine = list.find((a) => a.user_id === me);
  const others = list.filter((a) => a.user_id !== me);
  const targetName = list.find((a) => a.user_id === target)?.display_name ?? null;

  const reload = (): void => {
    accounts.refetch();
    activity.refetch();
  };

  /** Открыть форму — значит начать новое действие: исход прошлого больше не про него. */
  const startAction = (): void => {
    setError(null);
    setNotice(null);
  };

  const setActive = (id: string, active: boolean): void => {
    startAction();
    post<Account>(`/api/v1/accounts/${encodeURIComponent(id)}/active`, { active })
      .then(() => {
        setConfirmOff(null);
        setNotice(active ? 'Доступ включён.' : 'Доступ отключён.');
        reload();
      })
      .catch((e: unknown) => {
        setConfirmOff(null);
        setError(errorText(e));
      });
  };

  return (
    <>
      <BackdropBlur />
      <Modal title="Аккаунт" subtitle={user?.login ?? ''} width={620} onClose={onClose}>
        {accounts.error === undefined ? null : (
          <div className="val val-err" role="alert">
            <span>Учётные записи не загрузились: {accounts.error.message}</span>
          </div>
        )}
        {error === null ? null : (
          <div className="val val-err" role="alert">
            <span>{error}</span>
          </div>
        )}
        {notice === null ? null : (
          <div className="val val-info" role="status">
            <span>{notice}</span>
          </div>
        )}

        {/* ── я ─────────────────────────────────────────────────────────────── */}
        <div className={styles['meRow']}>
          <span className={`ava ${styles['avaBig']}`}>
            {user === null ? '—' : initials(user.display_name)}
          </span>
          <div className={styles['who']}>
            <div className="h3">{user?.display_name ?? ''}</div>
            <div className="meta">
              {user === null ? '' : `${user.login} · ${roleWord(user.role)}`}
              {mine?.last_login_at === undefined || mine.last_login_at === null
                ? ''
                : ` · вход ${shortMsk(mine.last_login_at)}`}
            </div>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              startAction();
              setPwFor(pwFor === me ? null : me);
              setAdding(false);
            }}
          >
            Пароль
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              void logout().then(() => {
                onClose();
                navigate('/login');
              });
            }}
          >
            Выйти
          </button>
        </div>

        {pwFor === me && me !== null ? (
          <PasswordForm
            userId={me}
            self={true}
            onDone={(msg) => {
              setPwFor(null);
              setNotice(msg);
            }}
            onCancel={() => {
              setPwFor(null);
            }}
          />
        ) : null}

        {/* ── остальные (только у администратора) ───────────────────────────── */}
        {!canManage ? null : (
          <>
            <hr className="hr" />
            <div className={styles['listHead']}>
              <span className="lbl" style={{ margin: 0 }}>
                Учётные записи
              </span>
              <div style={{ flex: 1 }} />
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  startAction();
                  setAdding(!adding);
                  setPwFor(null);
                }}
              >
                Добавить
              </button>
            </div>

            {adding ? (
              <AddAccountForm
                onDone={(msg) => {
                  setAdding(false);
                  setNotice(msg);
                  reload();
                }}
                onCancel={() => {
                  setAdding(false);
                }}
              />
            ) : null}

            {others.length === 0 ? (
              <span className="micro">Кроме вас никого не заведено.</span>
            ) : (
              <div className={styles['accounts']}>
                {others.map((a) => (
                  <div key={a.user_id} className={styles['accRow']}>
                    <span className="ava">{initials(a.display_name)}</span>
                    <button
                      type="button"
                      className={styles['accWho']}
                      aria-pressed={target === a.user_id}
                      onClick={() => {
                        setPicked(a.user_id);
                      }}
                    >
                      <span className={styles['accName']}>
                        {a.display_name}
                        {a.is_active ? null : <span className="pill st-hold">отключён</span>}
                      </span>
                      <span className="meta">
                        {`${a.login} · ${ROLE_LABEL[a.role]}`}
                        {a.last_login_at === null
                          ? ' · не входил'
                          : ` · вход ${shortMsk(a.last_login_at)}`}
                        {a.password_set_at === null ? ' · пароль не задан' : ''}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        startAction();
                        setPwFor(pwFor === a.user_id ? null : a.user_id);
                        setAdding(false);
                      }}
                    >
                      Пароль
                    </button>
                    {confirmOff === a.user_id ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => {
                            setActive(a.user_id, false);
                          }}
                        >
                          Точно
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => {
                            setConfirmOff(null);
                          }}
                        >
                          Нет
                        </button>
                      </>
                    ) : a.is_active ? (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => {
                          setConfirmOff(a.user_id);
                        }}
                      >
                        Отключить
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => {
                          setActive(a.user_id, true);
                        }}
                      >
                        Включить
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {pwFor !== null && pwFor !== me ? (
              // Форма стоит под списком, а не в строке, поэтому логин назван в подписи поля:
              // безымянное «Новый пароль» под перечнем людей — это пароль неизвестно кому.
              <PasswordForm
                userId={pwFor}
                self={false}
                subject={others.find((a) => a.user_id === pwFor)?.login ?? null}
                onDone={(msg) => {
                  setPwFor(null);
                  setNotice(msg);
                }}
                onCancel={() => {
                  setPwFor(null);
                }}
              />
            ) : null}

            <span className="micro">
              Учётные записи не удаляются: на них ссылается журнал решений, и он обязан оставаться
              читаемым. Отключённый не войдёт, а история цела.
            </span>
          </>
        )}

        {/* ── журнал действий ───────────────────────────────────────────────── */}
        <hr className="hr" />
        {/* Имя стоит в подписи ВСЕГДА: между карточкой «я» и журналом лежит список чужих
            учётных записей, и без имени непонятно, чей журнал показан. */}
        <span className="lbl" style={{ margin: 0 }}>
          Действия
          {targetName === null ? '' : ` · ${targetName}`}
        </span>
        <ActivityList
          entries={activity.data?.entries}
          loading={activity.loading}
          failed={activity.error !== undefined}
          onNavigate={onClose}
        />
        {target === me ? null : (
          <button
            type="button"
            className={styles['linkBtn']}
            onClick={() => {
              setPicked(null);
            }}
          >
            Показать свои
          </button>
        )}
      </Modal>
    </>
  );
}

function ActivityList({
  entries,
  loading,
  failed,
  onNavigate,
}: {
  entries: readonly AccountActivityEntry[] | undefined;
  loading: boolean;
  failed: boolean;
  onNavigate: () => void;
}): JSX.Element {
  if (failed) return <span className="micro">Журнал не загрузился.</span>;
  if (entries === undefined) return <span className="micro">{loading ? 'Загружаем…' : ''}</span>;
  if (entries.length === 0) return <span className="micro">Действий пока нет.</span>;

  return (
    <div className={styles['log']}>
      {entries.map((e) => (
        <div key={e.seq} className={styles['logRow']}>
          <span className={styles['logTime']}>{shortMsk(e.occurred_at)}</span>
          <span className={styles['logAct']}>{actionOf(e)}</span>
          {e.draft_id === null || e.brand === null ? (
            <span className="meta">{e.version_no === null ? '' : `v${String(e.version_no)}`}</span>
          ) : (
            <Link
              href={`/b/${encodeURIComponent(e.brand)}/draft/${encodeURIComponent(e.draft_id)}`}
              className="meta"
              onClick={onNavigate}
            >
              {e.version_no === null ? 'пост' : `пост v${String(e.version_no)}`}
            </Link>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Смена пароля. Своё меняется с подтверждением текущего, чужое назначается правом — какая из
 * веток действует, решает бэкенд по сессии, а форма лишь не спрашивает того, чего спрашивать
 * не должна: старого пароля чужой учётной записи администратор не знает.
 */
function PasswordForm({
  userId,
  self,
  subject = null,
  onDone,
  onCancel,
}: {
  userId: string;
  self: boolean;
  /** Логин владельца — стоит в подписи поля, когда пароль назначают не себе. */
  subject?: string | null;
  onDone: (message: string) => void;
  onCancel: () => void;
}): JSX.Element {
  const curId = useId();
  const nextId = useId();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const short = next.length > 0 && next.length < ACCOUNT_PASSWORD_MIN;
  const blocked = busy || next.length < ACCOUNT_PASSWORD_MIN || (self && current.length === 0);

  const submit = (): void => {
    setBusy(true);
    setError(null);
    post<Account>(`/api/v1/accounts/${encodeURIComponent(userId)}/password`, {
      current_password: self ? current : null,
      new_password: next,
    })
      .then(() => {
        onDone(
          self ? 'Пароль изменён. Другие сессии закрыты.' : 'Пароль назначен, сессии закрыты.',
        );
      })
      .catch((e: unknown) => {
        setBusy(false);
        setError(errorText(e));
      });
  };

  return (
    <div className={`card-flat ${styles['form']}`}>
      {self ? (
        <div>
          <label className="lbl" htmlFor={curId}>
            Текущий пароль
          </label>
          <input
            id={curId}
            className="inp"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => {
              setCurrent(e.target.value);
            }}
          />
        </div>
      ) : null}
      <div>
        <label className="lbl" htmlFor={nextId}>
          {subject === null ? 'Новый пароль' : `Новый пароль · ${subject}`}
        </label>
        <input
          id={nextId}
          className="inp"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => {
            setNext(e.target.value);
          }}
        />
        {short ? (
          <span className="micro">{`не короче ${String(ACCOUNT_PASSWORD_MIN)} символов`}</span>
        ) : null}
      </div>
      {error === null ? null : (
        <div className="val val-err" role="alert">
          <span>{error}</span>
        </div>
      )}
      <div className={styles['formActions']}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
          Отмена
        </button>
        <button
          type="button"
          className={blocked ? 'btn btn-sm btn-dis' : 'btn btn-pri btn-sm'}
          disabled={blocked}
          onClick={submit}
        >
          Сохранить
        </button>
      </div>
    </div>
  );
}

function AddAccountForm({
  onDone,
  onCancel,
}: {
  onDone: (message: string) => void;
  onCancel: () => void;
}): JSX.Element {
  const loginId = useId();
  const nameId = useId();
  const pwId = useId();
  const [login, setLogin] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<AccountRole>('approver');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Форма логина проверяется той же регуляркой, что и схема тела: логин попадает в актора
  // строк журнала и в подписываемый payload ключа, поэтому пробелов и юникода в нём нет.
  const loginIssue =
    login === '' || ACCOUNT_LOGIN_RE.test(login)
      ? null
      : 'Логин — строчная латиница, цифры, точка, дефис, подчёркивание, @ и +.';
  const blocked =
    busy ||
    loginIssue !== null ||
    login === '' ||
    name.trim() === '' ||
    password.length < ACCOUNT_PASSWORD_MIN;

  const submit = (): void => {
    setBusy(true);
    setError(null);
    post<Account>('/api/v1/accounts', {
      login,
      display_name: name.trim(),
      role,
      password,
    })
      .then(() => {
        onDone(`Учётная запись ${login} заведена.`);
      })
      .catch((e: unknown) => {
        setBusy(false);
        setError(errorText(e));
      });
  };

  return (
    <div className={`card-flat ${styles['form']}`}>
      <div className={styles['formGrid']}>
        <div>
          <label className="lbl" htmlFor={loginId}>
            Логин
          </label>
          <input
            id={loginId}
            className="inp"
            value={login}
            maxLength={64}
            onChange={(e) => {
              setLogin(e.target.value);
            }}
          />
        </div>
        <div>
          <label className="lbl" htmlFor={nameId}>
            Имя
          </label>
          <input
            id={nameId}
            className="inp"
            value={name}
            maxLength={128}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </div>
      </div>
      {loginIssue === null ? null : (
        <span className="val val-err" role="alert">
          {loginIssue}
        </span>
      )}
      <div className={styles['formGrid']}>
        <div>
          <span className="lbl">Роль</span>
          <div className="seg">
            <button
              type="button"
              className={role === 'approver' ? 'seg-o on' : 'seg-o'}
              aria-pressed={role === 'approver'}
              onClick={() => {
                setRole('approver');
              }}
            >
              Согласующий
            </button>
            <button
              type="button"
              className={role === 'admin' ? 'seg-o on' : 'seg-o'}
              aria-pressed={role === 'admin'}
              onClick={() => {
                setRole('admin');
              }}
            >
              Администратор
            </button>
          </div>
        </div>
        <div>
          <label className="lbl" htmlFor={pwId}>
            Пароль
          </label>
          <input
            id={pwId}
            className="inp"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
          />
          {password.length > 0 && password.length < ACCOUNT_PASSWORD_MIN ? (
            <span className="micro">{`не короче ${String(ACCOUNT_PASSWORD_MIN)} символов`}</span>
          ) : null}
        </div>
      </div>
      {error === null ? null : (
        <div className="val val-err" role="alert">
          <span>{error}</span>
        </div>
      )}
      <div className={styles['formActions']}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
          Отмена
        </button>
        <button
          type="button"
          className={blocked ? 'btn btn-sm btn-dis' : 'btn btn-pri btn-sm'}
          disabled={blocked}
          onClick={submit}
        >
          Завести
        </button>
      </div>
    </div>
  );
}
